import { useCallback, useContext } from 'react';
import TokensContext from '../TokensContext';
import { blynkApiClient } from '../api/apiClient';
import { ungzip } from 'pako';

const useApiCall = (token, route, parser, errorTitle) => {
    return useCallback( async () => {
        try {
            const response = await blynkApiClient.get(`${token}/${route}`);
            return parser(response.data[0]);
        } catch (error) {
            console.error(`error fetching ${errorTitle}`, error);
        }
    }, [token, route, parser, errorTitle]);
};

const useUpdateApiCall = (token, route, errorTitle) => {
    return async (value) => {
        if (token) {
            try {
                await blynkApiClient.get(`${token}/${route}?value=${value.toString()}`);
            } catch (error) {
                console.error(`error updating ${errorTitle}`, error);
            }
        } else {
            console.error(`token not set when updating ${errorTitle}`);
        }
    };
};

export const useBlynkApi = (heaterToken) => {
    const {thermometerToken} = useContext(TokensContext);

    const getTemperature = useApiCall(thermometerToken, 'get/V2', parseFloat, 'temperature');
    const getHeaterPosition = useApiCall(heaterToken, 'get/V8', parseInt, 'heater position');
    const updateHeaterPosition = useUpdateApiCall(heaterToken, 'update/v8', 'heater');
    const getDesiredTemperature = useApiCall(thermometerToken, 'get/V11', parseFloat, 'desired temperature');
    const updateDesiredTemperature = useUpdateApiCall(thermometerToken, 'update/V11', 'desired temperature');

    //gets the history of a pin, parsing it into a list of javascript objects with timestamp and the value set at the property valueName
    const getHistory = useCallback( async (token, pin, valueName) => {
        try {
            //get the current time and round it down to the nearest minute.
            const cacheBuster = Math.floor(Date.now() / (60 * 1000));
            

            const response = await blynkApiClient.get(`${token}/data/${pin}?t=${cacheBuster}`, {responseType: 'arraybuffer'});
            const decompressed = ungzip(response.data, {to: 'string'});
            const jsonData = parseHistory(decompressed, valueName);
            
            return jsonData;
        }
        catch (error) {
            console.error("error fetching position history", error);
            return [];
        }
    }, []);
    
    //gets temperature history
    const getTemperatureHistory = useCallback(() => getHistory(thermometerToken, 'V2', 'temperature'), [thermometerToken, getHistory]);
    //gets position history
    const getPositionHistory = useCallback(() => getHistory(heaterToken,'V12', 'position'), [heaterToken, getHistory]);

    //parses the history string, in the format "value,timestamp\n", into an array of javascript objects
    const parseHistory = (history, valueName) => {
        const entries = history.split('\n').filter((line) => line.length > 0);

        const parsedEntries = entries.map((entry) => {
            const [value, timestamp] = entry.split(',');
            const parsedEntry = {
                time: parseInt(timestamp)
            };
            parsedEntry[valueName] = parseFloat(value);

            return parsedEntry;
        });

        return parsedEntries;
    };

    return {
        getTemperature,
        getHeaterPosition,
        updateHeaterPosition,
        getDesiredTemperature,
        updateDesiredTemperature,
        getTemperatureHistory,
        getPositionHistory
    };
};
